import React, { useState, useEffect, useCallback } from "react";
import "./catalogue.css";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";

const LEVELS = [100, 200, 300, 400, 500, 600];
const SEMESTERS = [1, 2];
const TYPES = ["professional", "external"];

const emptyDept = { code: "", name: "" };
const emptyCourse = { code: "", title: "", unit_load: "", level: "", semester: "", type: "professional", department: "" };

function Catalogue() {
  const [tab, setTab] = useState("courses");
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [deptForm, setDeptForm] = useState(emptyDept);
  const [courseForm, setCourseForm] = useState(emptyCourse);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filterLevel, setFilterLevel] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 4000);
  };

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/departments`);
      const json = await res.json();
      if (json.success) setDepartments(json.departments);
    } catch { showMsg("error", "Failed to load departments."); }
  }, []);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterLevel) params.append("level", filterLevel);
      if (filterSemester) params.append("semester", filterSemester);
      if (filterType) params.append("type", filterType);
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/courses?${params}`);
      const json = await res.json();
      if (json.success) setCourses(json.courses);
    } catch { showMsg("error", "Failed to load courses."); }
    finally { setLoading(false); }
  }, [filterLevel, filterSemester, filterType]);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);
  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleCreateDept = async (e) => {
    e.preventDefault();
    if (!deptForm.code || !deptForm.name) return showMsg("error", "Code and name are required.");
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/departments`, {
        method: "POST",
        body: JSON.stringify(deptForm),
      });
      const json = await res.json();
      if (json.success) { showMsg("success", "Department created."); setDeptForm(emptyDept); fetchDepartments(); }
      else showMsg("error", json.error);
    } catch { showMsg("error", "Server error."); }
    finally { setSubmitting(false); }
  };

  const handleDeleteDept = async (_id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/departments/${_id}`, { method: "DELETE" });
      const json = await res.json();
      if (json.success) { showMsg("success", "Department deleted."); fetchDepartments(); }
      else showMsg("error", json.error);
    } catch { showMsg("error", "Server error."); }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    const { code, title, unit_load, level, semester, type } = courseForm;
    if (!code || !title || !unit_load || !level || !semester || !type)
      return showMsg("error", "All fields except department are required.");
    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/courses`, {
        method: "POST",
        body: JSON.stringify(courseForm),
      });
      const json = await res.json();
      if (json.success) { showMsg("success", "Course created."); setCourseForm(emptyCourse); fetchCourses(); }
      else showMsg("error", json.error);
    } catch { showMsg("error", "Server error."); }
    finally { setSubmitting(false); }
  };

  const handleDeleteCourse = async (_id) => {
    if (!window.confirm("Delete this course?")) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalogue/courses/${_id}`, { method: "DELETE" });
      const json = await res.json();
      if (json.success) { showMsg("success", "Course deleted."); fetchCourses(); }
      else showMsg("error", json.error);
    } catch { showMsg("error", "Server error."); }
  };

  const filteredCourses = courses.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.code.includes(q) || c.title.toLowerCase().includes(q);
  });

  return (
    <div className="cat_page">
      <div className="cat_header">
        <h2>Course Catalogue</h2>
        <p>Manage departments and courses. Only Super Admin can make changes.</p>
      </div>

      {message.text && (
        <div className={`cat_msg ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ type: "", text: "" })}>×</button>
        </div>
      )}

      <div className="cat_tabs">
        <button className={tab === "courses" ? "active" : ""} onClick={() => setTab("courses")}>Courses ({courses.length})</button>
        <button className={tab === "departments" ? "active" : ""} onClick={() => setTab("departments")}>Departments ({departments.length})</button>
      </div>

      {tab === "departments" && (
        <div className="cat_body">
          <div className="cat_form_card">
            <h3>Add Department</h3>
            <form onSubmit={handleCreateDept} className="cat_form">
              <div className="cat_field">
                <label>Code (e.g. pti)</label>
                <input type="text" placeholder="dept code" value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })} />
              </div>
              <div className="cat_field">
                <label>Name</label>
                <input type="text" placeholder="Department name" value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} />
              </div>
              <button type="submit" className="cat_btn" disabled={submitting}>{submitting ? "Saving..." : "Add Department"}</button>
            </form>
          </div>
          <div className="cat_list_card">
            <h3>All Departments</h3>
            <table className="cat_table">
              <thead><tr><th>Code</th><th>Name</th><th></th></tr></thead>
              <tbody>
                {departments.map((d) => (
                  <tr key={d._id}>
                    <td><span className="cat_code">{d.code}</span></td>
                    <td>{d.name}</td>
                    <td><button className="cat_del_btn" onClick={() => handleDeleteDept(d._id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "courses" && (
        <div className="cat_body">
          <div className="cat_form_card">
            <h3>Add Course</h3>
            <form onSubmit={handleCreateCourse} className="cat_form">
              <div className="cat_field">
                <label>Course Code</label>
                <input type="text" placeholder="e.g. pct224" value={courseForm.code} onChange={(e) => setCourseForm({ ...courseForm, code: e.target.value })} />
              </div>
              <div className="cat_field">
                <label>Title</label>
                <input type="text" placeholder="Course title" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} />
              </div>
              <div className="cat_field">
                <label>Unit Load</label>
                <input type="number" min="1" max="10" placeholder="Units" value={courseForm.unit_load} onChange={(e) => setCourseForm({ ...courseForm, unit_load: e.target.value })} />
              </div>
              <div className="cat_field">
                <label>Level</label>
                <select value={courseForm.level} onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}>
                  <option value="">Select level</option>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="cat_field">
                <label>Semester</label>
                <select value={courseForm.semester} onChange={(e) => setCourseForm({ ...courseForm, semester: e.target.value })}>
                  <option value="">Select semester</option>
                  {SEMESTERS.map((s) => <option key={s} value={s}>{s === 1 ? "First" : "Second"}</option>)}
                </select>
              </div>
              <div className="cat_field">
                <label>Type</label>
                <select value={courseForm.type} onChange={(e) => setCourseForm({ ...courseForm, type: e.target.value })}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="cat_field">
                <label>Department (optional)</label>
                <select value={courseForm.department} onChange={(e) => setCourseForm({ ...courseForm, department: e.target.value })}>
                  <option value="">None</option>
                  {departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                </select>
              </div>
              <button type="submit" className="cat_btn" disabled={submitting}>{submitting ? "Saving..." : "Add Course"}</button>
            </form>
          </div>

          <div className="cat_list_card">
            <div className="cat_filters">
              <input type="text" placeholder="Search code or title..." value={search} onChange={(e) => setSearch(e.target.value)} className="cat_search" />
              <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
                <option value="">All Levels</option>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <select value={filterSemester} onChange={(e) => setFilterSemester(e.target.value)}>
                <option value="">All Semesters</option>
                <option value="1">First</option>
                <option value="2">Second</option>
              </select>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                <option value="professional">Professional</option>
                <option value="external">External</option>
              </select>
            </div>
            {loading && <Loader />}
            {!loading && (
              <table className="cat_table">
                <thead>
                  <tr><th>Code</th><th>Title</th><th>Units</th><th>Level</th><th>Sem</th><th>Type</th><th>Dept</th><th></th></tr>
                </thead>
                <tbody>
                  {filteredCourses.map((c) => (
                    <tr key={c._id}>
                      <td><span className="cat_code">{c.code}</span></td>
                      <td>{c.title}</td>
                      <td>{c.unit_load}</td>
                      <td>{c.level}</td>
                      <td>{c.semester}</td>
                      <td><span className={`cat_type_badge ${c.type}`}>{c.type}</span></td>
                      <td>{c.department?.code || "-"}</td>
                      <td><button className="cat_del_btn" onClick={() => handleDeleteCourse(c._id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Catalogue;
